import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/fr';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import localeData from 'dayjs/plugin/localeData';
import arraySupport from 'dayjs/plugin/arraySupport';
import objectSupport from 'dayjs/plugin/objectSupport';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { EnglishTzCodes, FrenchTzCodes } from '@trip-ux/constants';

// Use browser's local timezone, falling back to America/Toronto for SSR/Node.js
const LOCAL_TIMEZONE = typeof Intl !== 'undefined' 
  ? Intl.DateTimeFormat().resolvedOptions().timeZone 
  : 'America/Toronto';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(localeData);
dayjs.extend(arraySupport);
dayjs.extend(objectSupport);
dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

/**
 * Convert a UTC timestamp to the user's local timezone for display purposes.
 * Use this instead of dayjs.utc() when formatting dates for user display
 * to ensure dates are shown in the user's local timezone rather than UTC.
 * 
 * @param input - The date input (UTC timestamp string or Date object)
 * @param locale - Optional locale for localization (defaults to 'en')
 * @returns A dayjs object in the user's local timezone
 */
export function dayjsLocal(input: dayjs.ConfigType, locale = 'en'): dayjs.Dayjs {
  return dayjs.utc(input).tz(LOCAL_TIMEZONE).locale(locale);
}

export function getTimeZoneAbbreviation(date: string | Date, _locale: string): string {
  const timeZoneAbbreviation = dayjs(date).tz(LOCAL_TIMEZONE).format('z');

  const timeZoneMap: Record<string, string> = {
    [EnglishTzCodes.ADT]: FrenchTzCodes.HAA,
    [EnglishTzCodes.CDT]: FrenchTzCodes.HAC,
    [EnglishTzCodes.EDT]: FrenchTzCodes.HAE,
    [EnglishTzCodes.PDT]: FrenchTzCodes.HAP,
    [EnglishTzCodes.MDT]: FrenchTzCodes.HAR,
    [EnglishTzCodes.NDT]: FrenchTzCodes.HAT,
    [EnglishTzCodes.AST]: FrenchTzCodes.HNA,
    [EnglishTzCodes.CST]: FrenchTzCodes.HNC,
    [EnglishTzCodes.EST]: FrenchTzCodes.HNE,
    [EnglishTzCodes.PST]: FrenchTzCodes.HNP,
    [EnglishTzCodes.MST]: FrenchTzCodes.HNR,
    [EnglishTzCodes.NST]: FrenchTzCodes.HNT,
  };

  return timeZoneMap[timeZoneAbbreviation] || timeZoneAbbreviation;
}

export { dayjs };
